import { useEffect, useState } from "react";
import {
  Card,
  Row,
  Col,
  Select,
  Input,
  Button,
  Table,
  Tag,
  DatePicker,
  Typography,
} from "antd";
import { FilterOutlined, CalendarOutlined, ReloadOutlined, InfoCircleOutlined } from "@ant-design/icons";
import {
  RiCheckboxCircleFill,
  RiTimeFill,
  RiAlertFill,
} from "react-icons/ri";
import moment from "moment";
import "moment/locale/id";

import MyPage from "../../../../../components/pages/Page";
import { useFetch } from "../../../../../hooks/useFetch";
import "../table-compact.css";

const { Option } = Select;
const { RangePicker } = DatePicker;
const { Text } = Typography;

const toYmd = (m) => (m ? m.format("YYYYMMDD") : "");

const parseDT = (v) => {
  if (!v && v !== 0) return null;
  const m = moment(
    v,
    ["YYYYMMDDHHmmss", "YYYYMMDD", "YYYY-MM-DD HH:mm:ss", "YYYY-MM-DD"],
    true
  );
  return m.isValid() ? m : null;
};

const fmtD = (v) => {
  const m = parseDT(v);
  return m ? m.format("DD/MM/YYYY") : "-";
};

const fmtDT = (v) => {
  const m = parseDT(v);
  return m ? m.format("DD/MM/YYYY") : "-";
};

const fmtTime = (v) => {
  const m = parseDT(v);
  return m ? m.format("HH:mm:ss") : "-";
};

const calcUmurNum = (r) => {
  const base =
    parseDT(r.tanggal_hawb) ||
    parseDT(r.hawb_date) ||
    parseDT(r.wk_in) ||
    parseDT(String(r.tanggal_dok_in ?? ""));
  if (!base) return 0;
  const diff = moment().diff(base.clone().startOf("day"), "days");
  return diff < 0 ? 0 : diff;
};

const buildQuery = ({ noDok, range, page = 1, perPage = 5 }) => {
  const params = new URLSearchParams();
  if (noDok) params.append("no_dok", noDok);
  const [start, end] = range || [];
  if (start) params.append("start_date", toYmd(start));
  if (end) params.append("end_date", toYmd(end));
  params.append("page", page);
  params.append("perPage", perPage);
  return `api/v1/beacukai/ebandon?${params.toString()}`;
};

// respons API: { data: { data: [...], total, page, perPage, totalPage, statistic: [{label, value}] } }
const mapRow = (r, i) => ({
  key: r.no_hawb ? `${r.no_hawb}-${i}` : i,
  statusBtd: r.status_barang ?? "-",
  umurBtd: calcUmurNum(r),
  noBcf: r.nomor_dok_in ?? "-",
  tglBcf: r.wk_in ?? r.tanggal_dok_in ?? null,
  noBc: r.no_bc11 ?? "-",
  tglBc: r.tanggal_bc11 ?? null,
  noHawb: r.no_hawb ?? r.hawb_number ?? "-",
  tglHawb: r.tanggal_hawb ?? r.hawb_date ?? null,
  consignee: r.consignee ?? r.exportir_name ?? "-",
  tglGateIn: r.wk_in ?? null,
  tglGateOut: r.wk_out ?? null,
  raw: r,
});

const DEFAULT_RANGE = [moment().subtract(30, "days"), moment()];
const PER_PAGE = 5;

const STAT_CARDS = [
  { match: "1 s.d. 30", fallback: "1 s.d. 30 Hari semenjak menjadi barang Abandon", bg: "#e6f7ff", border: "#91d5ff", color: "#096dd9", Icon: RiCheckboxCircleFill },
  { match: "31 s.d. 45", fallback: "31 s.d. 45 Hari", bg: "#fffbe6", border: "#ffe58f", color: "#d4b106", Icon: RiTimeFill },
  { match: "45 s.d. 60", fallback: "45 s.d. 60 Hari", bg: "#fff1f0", border: "#ffa39e", color: "#cf1322", Icon: RiAlertFill },
];

export default () => {
  const [statusFilter, setStatusFilter] = useState("");
  const [docNumber, setDocNumber] = useState("");
  const [range, setRange] = useState(DEFAULT_RANGE);
  const [page, setPage] = useState(1);
  const [query, setQuery] = useState(() =>
    buildQuery({ noDok: "", range: DEFAULT_RANGE, page: 1, perPage: PER_PAGE })
  );
  const [rows, loading] = useFetch(query);
  const [lastUpdated, setLastUpdated] = useState(() => moment());

  useEffect(() => {
    if (rows) setLastUpdated(moment());
  }, [rows]);

  const argsFor = (p) => ({
    noDok: docNumber.trim(),
    range,
    page: p,
    perPage: PER_PAGE,
  });

  const doSearch = () => {
    setPage(1);
    setQuery(buildQuery(argsFor(1)));
  };

  const onRangeChange = (dates) => {
    setRange(dates);
    if (dates?.[0] && dates?.[1]) {
      setPage(1);
      setQuery(
        buildQuery({ noDok: docNumber.trim(), range: dates, page: 1, perPage: PER_PAGE })
      );
    }
  };

  const doRefresh = () => {
    setQuery(`${buildQuery(argsFor(page))}&_=${Date.now()}`);
  };

  const handleTableChange = (pagination) => {
    const nextPage = pagination.current;
    setPage(nextPage);
    setQuery(buildQuery(argsFor(nextPage)));
  };

  const apiRows = Array.isArray(rows?.data) ? rows.data : [];
  const total = rows?.total ?? 0;
  const statistic = Array.isArray(rows?.statistic) ? rows.statistic : [];

  const filtered = statusFilter
    ? apiRows.filter((r) =>
        String(r.status_barang ?? "").toLowerCase().includes(statusFilter.toLowerCase())
      )
    : apiRows;
  const dataSource = filtered.map(mapRow);

  const statVal = (i, match) => {
    if (statistic[i] && typeof statistic[i].value !== "undefined") return statistic[i].value;
    const found = statistic.find((s) => String(s.label || "").includes(match));
    return found ? found.value : 0;
  };

  const columns = [
    {
      title: "No",
      dataIndex: "key",
      key: "key",
      width: 50,
      render: (_, __, idx) => (page - 1) * PER_PAGE + idx + 1,
    },
    {
      title: "Status BTD",
      dataIndex: "statusBtd",
      key: "statusBtd",
      render: (status) => {
        const s = String(status ?? "");
        let color = "default";
        if (s.includes("Selesai")) color = "blue";
        else if (s.includes("Tetap BTD")) color = "orange";
        else if (s.includes("BMMN")) color = "warning";
        else if (s.includes("Tarik")) color = "purple";
        else if (s.includes("Musnah")) color = "error";
        else if (s.includes("Gate Out") || s === "Out") color = "green";
        else if (s.includes("Gate In") || s.includes("In")) color = "yellow";

        return <Tag color={color}>{s || "-"}</Tag>;
      },
    },
    {
      title: "Umur BTD (satuan hari)",
      dataIndex: "umurBtd",
      key: "umurBtd",
      render: (text) => `${text} hari`,
    },
    {
      title: "Nomor BCF1.5",
      dataIndex: "noBcf",
      key: "noBcf",
      render: (v) => v ?? "-",
    },
    {
      title: "Tanggal BCF1.5",
      dataIndex: "tglBcf",
      key: "tglBcf",
      render: (text) =>
        text ? (
          <div>
            {fmtTime(text)}
            <br />
            <span style={{ fontSize: "11px", color: "#8c8c8c" }}>{fmtDT(text)}</span>
          </div>
        ) : (
          "-"
        ),
    },
    {
      title: "Nomor BC1.1",
      dataIndex: "noBc",
      key: "noBc",
      render: (v) => v ?? "-",
    },
    {
      title: "Tanggal BC1.1",
      dataIndex: "tglBc",
      key: "tglBc",
      render: (text) =>
        text ? (
          <div>
            {fmtTime(text)}
            <br />
            <span style={{ fontSize: "11px", color: "#8c8c8c" }}>{fmtDT(text)}</span>
          </div>
        ) : (
          "-"
        ),
    },
    {
      title: "Nomor HAWB",
      dataIndex: "noHawb",
      key: "noHawb",
    },
    {
      title: "Tanggal HAWB",
      dataIndex: "tglHawb",
      key: "tglHawb",
      render: (text) =>
        text ? (
          <div>
            {fmtTime(text)}
            <br />
            <span style={{ fontSize: "11px", color: "#8c8c8c" }}>{fmtD(text)}</span>
          </div>
        ) : (
          "-"
        ),
    },
    {
      title: "Consignee",
      dataIndex: "consignee",
      key: "consignee",
    },
    {
      title: "Tanggal Gate In",
      dataIndex: "tglGateIn",
      key: "tglGateIn",
      render: (text) => (text ? fmtD(text) : "-"),
    },
    {
      title: "Tanggal Gate Out",
      dataIndex: "tglGateOut",
      key: "tglGateOut",
      render: (text) => (text ? fmtD(text) : "-"),
    },
  ];

  return (
    <MyPage title={"Ebandon"}>
      <div
        className="dashboard-filters"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "12px",
          marginBottom: "20px",
          paddingBottom: "4px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <span style={{ fontSize: "13px", color: "#1e293b", fontWeight: 500 }}>
            Filter Tampilan:
          </span>

          <Select
            defaultValue="harian"
            style={{ width: 100 }}
            size="small"
            options={[
              { value: "harian", label: "Harian" },
              { value: "mingguan", label: "Mingguan" },
            ]}
          />

          <Select
            defaultValue="bulanan"
            style={{ width: 105 }}
            size="small"
            options={[
              { value: "bulanan", label: "Bulanan" },
              { value: "tahunan", label: "Tahunan" },
            ]}
          />

          <RangePicker
            size="small"
            style={{ width: 350, flexShrink: 0 }}
            prefixIcon={
              <CalendarOutlined
                style={{ color: "#64748b", fontSize: "12px" }}
              />
            }
            suffixIcon={
              <CalendarOutlined
                style={{ color: "#64748b", fontSize: "12px" }}
              />
            }
            separator=" - "
            value={range}
            onChange={onRangeChange}
            format="DD MMMM YYYY"
          />
        </div>

        <div style={{ fontSize: "12px", color: "#475569" }}>
          Terakhir Diperbarui:{" "}
          <span style={{ fontWeight: 500 }}>
            {lastUpdated.clone().locale("id").format("DD MMMM YYYY, HH:mm [WIB]")}
          </span>
        </div>
      </div>

      <Row gutter={[12, 12]} justify="space-between" style={{ marginBottom: "24px" }}>
        {STAT_CARDS.map((c, i) => (
          <Col xs={24} md={8} key={c.match}>
            <Card
              size="small"
              style={{ background: c.bg, border: `1px solid ${c.border}` }}
            >
              <Row justify="space-between" align="middle">
                <Text strong style={{ color: c.color }}>
                  <c.Icon size={16} style={{ verticalAlign: "-3px", marginRight: 4 }} /> {statistic[i]?.label ?? c.fallback}
                </Text>
                <InfoCircleOutlined style={{ color: "#bfbfbf" }} />
              </Row>
              <div style={{ marginTop: 8, fontSize: 12 }}>
                Jumlah HAWB: <strong>{statVal(i, c.match)}</strong>
              </div>
              <div style={{ marginTop: 4, fontSize: 10, color: "#8c8c8c" }}>
                Status Rincian di Bawah
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      <Card style={{ marginBottom: "24px" }} bodyStyle={{ padding: "16px" }}>
        <Row gutter={16} justify="space-between">
          <Col span={3} style={{ textAlign: "right", fontWeight: "bold" }}>
            Status BTD
          </Col>
          <Col span={6}>
            <Select
              showSearch
              style={{ width: "100%" }}
              placeholder="Pilih Status"
              onChange={(value) => setStatusFilter(value)}
              allowClear
              value={statusFilter || undefined}
            >
              <Option value="Cacah">
                Selesai Cacah (Nomor BCP dan/atau Nomor BA Cacah)
              </Option>
              <Option value="Tetap BTD">Tetap BTD (Nomor Kep BTD)</Option>
              <Option value="Tetap BMMN">Tetap BMMN (Nomor Kep BMMN)</Option>
              <Option value="Tarik TPP">Tarik TPP (Nomor BAST)</Option>
            </Select>
          </Col>
          <Col span={8}>
            <Input
              placeholder="Masukkan Nomor Dokumen Terkait..."
              value={docNumber}
              onChange={(e) => setDocNumber(e.target.value)}
              onPressEnter={doSearch}
              allowClear
            />
          </Col>
          <Col span={4}>
            <Button
              type="primary"
              icon={<FilterOutlined />}
              block
              style={{ backgroundColor: "#262626" }}
              onClick={doSearch}
              loading={loading}
            >
              Terap Filter
            </Button>
          </Col>
        </Row>
      </Card>

      <div
        style={{
          marginBottom: "12px",
          fontWeight: "bold",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <span>
          <span style={{ marginRight: "8px" }}>📋</span> Rincian Data Abandon &
          Pergerakan Barang
        </span>
        <Button
          size="small"
          icon={<ReloadOutlined />}
          onClick={doRefresh}
          loading={loading}
        >
          Refresh
        </Button>
      </div>

      <Table
        className="bv2-compact"
        dataSource={dataSource}
        columns={columns}
        bordered
        size="small"
        loading={loading}
        pagination={{
          current: page,
          pageSize: PER_PAGE,
          total,
          showSizeChanger: false,
          showTotal: (t) => `Total ${t} data`,
        }}
        onChange={handleTableChange}
        scroll={{ x: "max-content" }}
      />
    </MyPage>
  );
};
